import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

/**
 * All padding lives on the wrapper so every control is the same height.
 * A Select renders its value as a <div>, not an <input> — without resetting it too,
 * selects end up taller than text fields because the inner padding stacks on top.
 */
const INNER_RESET = {
  '& input': { p: 0 },
  '& textarea': { p: 0 },
  '& .MuiSelect-select': { p: 0, pr: '22px', minHeight: 'auto' },
};

const CONTROL_SX = {
  width: '100%',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  p: '10px 12px',
  background: 'var(--card-2)',
  color: 'var(--ink)',
  fontSize: 13,
  '&.Mui-focused': { outline: '2px solid var(--mint)', outlineOffset: '1px', background: 'var(--card)' },
  ...INNER_RESET,
};

/** Mono uppercase micro-label sitting above a control. */
export function FieldLabel({ children, required }) {
  return (
    <Box
      sx={{
        fontFamily: 'var(--mono)',
        fontSize: 10.5,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: 'var(--muted)',
        mb: '6px',
      }}
    >
      {children}
      {required && <Box component="span" sx={{ color: 'var(--crit)' }}> *</Box>}
    </Box>
  );
}

/** Label + text input. */
export function TextField({ label, required, sx, ...rest }) {
  return (
    <Box component="label" sx={{ display: 'block', ...sx }}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <InputBase sx={CONTROL_SX} {...rest} />
    </Box>
  );
}

/** Label + native-feel select fed from an options array. `inputSx` tunes the control box. */
export function SelectField({ label, options = [], value, onChange, renderValue, sx, inputSx, ...rest }) {
  return (
    <Box component="label" sx={{ display: 'block', ...sx }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Select
        variant="standard"
        value={value ?? ''}
        onChange={onChange}
        input={<InputBase sx={{ ...CONTROL_SX, ...inputSx }} />}
        displayEmpty
        renderValue={renderValue}
        MenuProps={{ slotProps: { paper: { sx: { maxHeight: 340 } } } }}
        {...rest}
      >
        {options.map((o) => {
          const val = typeof o === 'object' ? o.value : o;
          const lbl = typeof o === 'object' ? o.label : o;
          return (
            <MenuItem key={String(val)} value={val}>
              {lbl}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
}

/** Compact top-bar select (date range, geo filters, list filters). */
export function PillSelect({ value, onChange, options, sx, renderValue, ...rest }) {
  // MUI treats '' as "nothing selected" and renders an empty pill, which is wrong for
  // a filter whose empty state is a real choice ("Any stage"). Show that option's label.
  const labelFor = (v) => {
    const hit = options.find((o) => (typeof o === 'object' ? o.value : o) === v);
    return hit === undefined ? v : typeof hit === 'object' ? hit.label : hit;
  };

  return (
    <Select
      variant="standard"
      value={value}
      onChange={onChange}
      displayEmpty
      renderValue={renderValue || labelFor}
      input={
        <InputBase
          sx={{
            border: '1px solid var(--border)',
            background: 'var(--card)',
            borderRadius: '12px',
            p: '9px 12px',
            boxShadow: 'var(--shadow)',
            color: 'var(--ink)',
            fontFamily: 'var(--mono)',
            fontSize: 12,
            ...INNER_RESET,
            ...sx,
          }}
        />
      }
      MenuProps={{ slotProps: { paper: { sx: { maxHeight: 340 } } } }}
      {...rest}
    >
      {options.map((o) => {
        const val = typeof o === 'object' ? o.value : o;
        const lbl = typeof o === 'object' ? o.label : o;
        return (
          <MenuItem key={String(val)} value={val}>
            {lbl}
          </MenuItem>
        );
      })}
    </Select>
  );
}

export { CONTROL_SX };
